/**
 * 이미지 압축 유틸리티
 * 브라우저 Canvas API를 사용하여 이미지 압축
 */

interface CompressionOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
  maxSizeMB?: number
}

export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.85,
    maxSizeMB = 2
  } = options

  // 이미지가 아니면 원본 반환
  if (!file.type.startsWith('image/')) {
    return file
  }

  // HEIC/HEIF 파일 처리 (iOS 사진)
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    // HEIC는 브라우저가 직접 지원하지 않으므로 원본 반환
    // 서버에서 처리하거나 별도 라이브러리 필요
    return file
  }

  // 큰 파일에 대한 초기 품질 조정
  let initialQuality = quality
  const fileSizeMB = file.size / (1024 * 1024)
  
  if (fileSizeMB > 10) {
    // 10MB 이상이면 품질을 더 낮춰서 시작
    initialQuality = Math.min(0.6, quality)
  } else if (fileSizeMB > 5) {
    // 5MB 이상이면 품질을 약간 낮춰서 시작
    initialQuality = Math.min(0.7, quality)
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    
    reader.onload = (e) => {
      const img = new Image()
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          reject(new Error('Canvas context not available'))
          return
        }

        // 새로운 크기 계산
        let width = img.width
        let height = img.height

        // 비율 유지하면서 가로 기준으로 크기 조정
        if (width > maxWidth) {
          const aspectRatio = width / height
          width = maxWidth
          height = width / aspectRatio
        }

        // 세로가 여전히 maxHeight를 초과하면 세로 기준으로 재조정
        if (height > maxHeight) {
          const aspectRatio = width / height
          height = maxHeight
          width = height * aspectRatio
        }

        // Canvas 크기 설정
        canvas.width = width
        canvas.height = height

        // 이미지 그리기
        ctx.drawImage(img, 0, 0, width, height)

        // Blob으로 변환
        canvas.toBlob(
          async (blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'))
              return
            }

            // 파일 크기 확인
            const sizeInMB = blob.size / (1024 * 1024)
            
            // 목표 크기보다 크면 quality를 더 낮춰서 재압축
            if (sizeInMB > maxSizeMB && initialQuality > 0.3) {
              const lowerQuality = Math.max(0.3, initialQuality - 0.15)
              const result = await compressImage(file, {
                ...options,
                quality: lowerQuality
              })
              resolve(result)
              return
            }

            // 압축된 파일 생성
            const compressedFile = new File(
              [blob],
              file.name,
              {
                type: blob.type || 'image/jpeg',
                lastModified: Date.now()
              }
            )

            // 원본보다 크면 원본 반환
            if (compressedFile.size >= file.size) {
              resolve(file)
            } else {
              resolve(compressedFile)
            }
          },
          file.type === 'image/png' ? 'image/png' : 'image/jpeg',
          initialQuality
        )
      }

      img.onerror = () => {
        reject(new Error('Failed to load image'))
      }

      img.src = e.target?.result as string
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * 이미지 파일 검증
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // 파일 크기 체크 (50MB까지 허용 - 압축 후 처리)
  if (file.size > 50 * 1024 * 1024) {
    return { valid: false, error: '이미지 크기는 50MB 이하여야 합니다.' }
  }

  // 파일 타입 체크
  const acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
  if (!acceptedTypes.includes(file.type)) {
    return { valid: false, error: '지원하지 않는 이미지 형식입니다. (JPG, PNG, WebP, GIF만 가능)' }
  }

  return { valid: true }
}

/**
 * 이미지 미리보기 URL 생성
 */
export function createImagePreview(file: File): string {
  return URL.createObjectURL(file)
}

/**
 * 이미지 미리보기 URL 해제 (메모리 누수 방지)
 */
export function revokeImagePreview(url: string): void {
  URL.revokeObjectURL(url)
}